import { ApplicationQueryKeys } from "./ApplicationQueries";
import { SATOSPortal } from "@/data-adapter/SATOSPortal";
import {
	GroupItem,
	ShiftEventCrew,
	ShiftEventResponse,
	ShiftEventsPagedResponse,
	ShiftEventsQuery,
	ShiftOptions,
} from "@/features/shift/model/Shift/Shift";
import { ShiftSummary } from "@/features/shift/model/Shift/ShiftSummary";
import { useQuery } from "@tanstack/react-query";

import {
	ShiftName,
	ShiftSettingsResponse,
	ShiftSystem,
} from "@/model/Application/Settings/SettingsResponseTypes";

export const useFetchShiftNames = () => {
	return useQuery<ShiftName[], unknown>({
		queryKey: [ShiftQueryKeys.shiftNames],
		queryFn: () => SATOSPortal.GetShiftNames(),
	});
};

export const useFetchShiftSystems = () => {
	return useQuery<ShiftSystem[], unknown>({
		queryKey: [ShiftQueryKeys.shiftSystems],
		queryFn: () => SATOSPortal.GetShiftSystems(),
	});
};

export const useFetchShiftCrews = () => {
	return useQuery<ShiftEventCrew[], unknown>({
		queryKey: [ShiftQueryKeys.shiftCrews],
		queryFn: () => SATOSPortal.GetShiftCrews(),
	});
};

export const useFetchShiftGroups = () => {
	return useQuery<GroupItem[], unknown>({
		queryKey: [ApplicationQueryKeys.user, ShiftQueryKeys.shift],
		queryFn: () => SATOSPortal.GetShiftGroups(),
	});
};

export const useFetchShiftSettings = () => {
	return useQuery<ShiftSettingsResponse>({
		queryKey: [ApplicationQueryKeys.shiftSettings],
		queryFn: () => SATOSPortal.GetShiftSettings(),
	});
};

export const useFetchShiftOptions = () => {
	return useQuery<ShiftOptions>({
		queryKey: [ApplicationQueryKeys.user, ShiftQueryKeys.shift],
		queryFn: () => SATOSPortal.GetShiftOptions(),
	});
};
export const useFetchShift = (
	queryOptions: ShiftEventsQuery,
	options?: { enabled?: boolean },
) => {
	return useQuery<ShiftEventsPagedResponse, unknown>({
		queryKey: [ApplicationQueryKeys.user, ShiftQueryKeys.shift, queryOptions],
		queryFn: () => SATOSPortal.GetShiftEvents(queryOptions),
		enabled: options?.enabled ?? true,
	});
};

export const useFetchShiftEvent = (shiftEventId?: number) => {
	return useQuery<ShiftEventResponse, unknown>({
		queryKey: [
			ApplicationQueryKeys.user,
			ShiftQueryKeys.shiftEvent,
			shiftEventId,
		],
		queryFn: () => SATOSPortal.GetShiftEvent(shiftEventId as number),
		enabled: typeof shiftEventId === "number" && Number.isFinite(shiftEventId),
	});
};

export const useFetchShiftSummary = (
	groupId: number,
	isDialogueOpen: boolean,
) => {
	return useQuery<ShiftSummary, unknown>({
		queryKey: [ShiftQueryKeys.shiftSummary, groupId],
		queryFn: () => SATOSPortal.GetShiftSummary(groupId),
		enabled: isDialogueOpen,
	});
};

export class ShiftQueryKeys {
	static readonly shift = "Shift";
	static readonly groups = "Groups";
	static readonly shiftEvents = "Shift Events";
	static readonly shiftEvent = "Shift Event";
	static readonly shiftLog = "Shift Log";
	static readonly shiftHandover = "Shift Handover";
	static readonly shiftOutOfService = "Shift Out Of Service";
	static readonly shiftNames = "shiftNames";
	static readonly shiftSystems = "shiftSystems";
	static readonly options = "Options";
	static readonly shiftSearch = "Shift Search";
	static readonly shiftSummary = "ShiftSummary";
	static readonly shiftCrews = "ShiftCrews";
}

export interface FetchShiftOptionsResult {
	data: ShiftOptions | undefined;
	isLoading: boolean;
}
